import { useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";

const parseSettingNumber = (raw: any): number | null => {
  if (raw === undefined || raw === null) return null;
  let parsed = raw;

  if (typeof parsed === "object" && parsed !== null) {
    if ("userSettings" in parsed) parsed = parsed.userSettings;
    else if ("tenantSettings" in parsed) parsed = parsed.tenantSettings;
    else if ("settings" in parsed) parsed = parsed.settings;
    else if ("getSettings" in parsed) parsed = parsed.getSettings;
  }
  if (typeof parsed === "object" && parsed !== null && "settings" in parsed) {
    parsed = parsed.settings;
  }

  if (parsed === null || parsed === undefined || parsed === "") return null;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = parseInt(parsed, 10);
    }
  }

  if (parsed === null || parsed === undefined || parsed === "") return null;

  const num = Number(parsed);
  if (!isNaN(num) && num >= 0 && num <= 20) {
    return num;
  }
  return null;
};

/**
 * Custom hook to fetch pastYears and futureYears settings with user -> tenant -> global fallback.
 * Default fallback is 3 years past and 3 years future if settings are not found.
 */
export function useDateRangeSettings(pastKey?: string, futureKey?: string) {
  const singularPastKey = pastKey?.endsWith("years")
    ? pastKey.slice(0, -1)
    : undefined;
  const singularFutureKey = futureKey?.endsWith("years")
    ? futureKey.slice(0, -1)
    : undefined;

  // Past key queries
  const { data: userPast } = useQuery<{ userSettings: any }>(
    queries.userSettings,
    {
      variables: { key: pastKey },
      skip: !pastKey,
    },
  );
  const { data: tenantPast } = useQuery<{ tenantSettings: any }>(
    queries.tenantSettings,
    {
      variables: { key: pastKey },
      skip: !pastKey,
    },
  );
  const { data: globalPast } = useQuery<{ getSettings: any }>(
    queries.getSettings,
    {
      variables: { key: pastKey },
      skip: !pastKey,
    },
  );

  // Singular past key queries (fallback if plural returned null)
  const { data: userPastSingular } = useQuery<{ userSettings: any }>(
    queries.userSettings,
    {
      variables: { key: singularPastKey },
      skip: !singularPastKey,
    },
  );
  const { data: globalPastSingular } = useQuery<{ getSettings: any }>(
    queries.getSettings,
    {
      variables: { key: singularPastKey },
      skip: !singularPastKey,
    },
  );

  // Future key queries
  const { data: userFuture } = useQuery<{ userSettings: any }>(
    queries.userSettings,
    {
      variables: { key: futureKey },
      skip: !futureKey,
    },
  );
  const { data: tenantFuture } = useQuery<{ tenantSettings: any }>(
    queries.tenantSettings,
    {
      variables: { key: futureKey },
      skip: !futureKey,
    },
  );
  const { data: globalFuture } = useQuery<{ getSettings: any }>(
    queries.getSettings,
    {
      variables: { key: futureKey },
      skip: !futureKey,
    },
  );

  // Singular future key queries
  const { data: userFutureSingular } = useQuery<{ userSettings: any }>(
    queries.userSettings,
    {
      variables: { key: singularFutureKey },
      skip: !singularFutureKey,
    },
  );
  const { data: globalFutureSingular } = useQuery<{ getSettings: any }>(
    queries.getSettings,
    {
      variables: { key: singularFutureKey },
      skip: !singularFutureKey,
    },
  );

  const pastYearsVal =
    parseSettingNumber(userPast) ??
    parseSettingNumber(tenantPast) ??
    parseSettingNumber(globalPast) ??
    parseSettingNumber(userPastSingular) ??
    parseSettingNumber(globalPastSingular);

  const futureYearsVal =
    parseSettingNumber(userFuture) ??
    parseSettingNumber(tenantFuture) ??
    parseSettingNumber(globalFuture) ??
    parseSettingNumber(userFutureSingular) ??
    parseSettingNumber(globalFutureSingular);

  const pastYears = pastYearsVal !== null ? pastYearsVal : 3;
  const futureYears = futureYearsVal !== null ? futureYearsVal : 3;

  return { pastYears, futureYears };
}
